import { AfterViewChecked, Component, EventEmitter, Inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { enableDebugTools } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { FirebaseService } from 'src/app/commons/firebase.service';
import { FunctionsService } from 'src/app/data/functions.service';
import { ItemCompraRecorrente, OrigemCompraRecorrente } from '../models/compra-recorrente.model';
import { RegistroClienteComponent } from '../cliente/registro/registro-cliente.component';
import { FullscreenModalContextualSearchComponent } from '../fullscreen-modal-contextual-search/fullscreen-modal-contextual-search.component';

/** Acima disso, uma lista deixa de ser o chat de busca contextual (no máximo
 * 10 itens hoje) para ser tratada como importação em lote, mesmo que
 * `isBulkImport` não tenha sido explicitamente sinalizado por quem abriu o
 * componente. */
const LIMITE_ITENS_CHAT = 10;
/** Tamanho de cada lote de criação concorrente no fluxo de importação em
 * lote — evita um único `Promise.all` de até 200 promises de uma vez.
 * Baixo de propósito (não é sobre performance do browser): toda negociação
 * do MESMO comprador escreve nos MESMOS documentos compartilhados
 * (`clientes/{uid}`, `parametrizacao/dashboard_negociacoes`) dentro da
 * transação do backend — poucas escritas concorrentes já bastam pra gerar
 * contenção real (`Transaction lock timeout`, mais estrito no emulador local
 * de Firestore que em produção). Ver ADR pendente sobre mover chamadas
 * externas (Telegram/DataForSEO) para fora da transação — fix estrutural,
 * fora do escopo desta mitigação client-side. */
const TAMANHO_LOTE_CRIACAO = 3;
/** Tentativas extras (além da primeira) para um item que falhar na criação —
 * cobre exatamente o caso de falha transiente por contenção de escrita no
 * Firestore, que tende a se resolver numa nova tentativa alguns instantes
 * depois. */
const TENTATIVAS_EXTRAS_POR_ITEM = 2;
const ESPERA_ENTRE_TENTATIVAS_MS = 1500;

function aguardar(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export interface ProdutoCompostoInicial {
  ean: string;
  nome: string;
  imagemUrl?: string;
  /** `number` no chat de busca contextual (`contextualSearch`); `string` no
   * fluxo de importação em lote (`bulk-list-import`) — ambos só circulam
   * entre `FunctionsService` e a URL de `addNegociacaoComposta`. */
  prompt_id?: number | string;
  quantidade?: number;
  valor_pretendido?: number;
  candidatos?: { ean: string; nome: string; imagem?: { url: string }; [key: string]: any }[];
}

export interface ResumoCriacaoLote {
  total: number;
  sucesso: number;
  falhas: number;
  itensComFalha: { index: number; nome: string; error: any }[];
}

@Component({
  selector: 'app-adiciona-negociacao-composta',
  templateUrl: './adiciona-negociacao-composta.component.html',
  styleUrls: ['./adiciona-negociacao-composta.component.css'],
  providers: [MessageService]
})
export class AdicionaNegociacaoCompostaComponent implements AfterViewChecked {
  @ViewChild('negociacaoStepper') negociacaoStepper!: MatStepper;
  @Input() initialProducts: ProdutoCompostoInicial[] = [];
  /** Sinaliza o fluxo de importação de lista de compras em lote (upload de
   * arquivo), em vez do chat de busca contextual — ver `isBulkFlow`. */
  @Input() isBulkImport: boolean = false;
  public loading: boolean = false;
  public negociacaoFormGroup: FormGroup;
  public usuarioEstaLogado: boolean;
  public usuarioJaTemUmaConta: boolean;
  public negociacao: string | null = null;
  public isOpen = true;
  @Output() closed = new EventEmitter<void>();

  /** Candidatos do catálogo por linha do `productFormArray` (índice a
   * índice) — dado auxiliar de exibição, não faz parte do form. */
  public productCandidates: { ean: string; nome: string; imagem?: { url: string }; [key: string]: any }[][] = [];
  /** Cobre a fase de upload/extração (antes dos itens chegarem), quando
   * `UploadListaComprasComponent` abre este componente já em processamento —
   * ver `receiveBulkImportResult`/`receiveBulkImportError`. */
  public uploading: boolean = false;
  public uploadError: string | null = null;
  /** Progresso e resumo da criação em lotes (fluxo de importação em lote). */
  public criacaoProgresso: { concluido: number; total: number } | null = null;
  public criacaoResumo: ResumoCriacaoLote | null = null;

  /** "Deseja salvar esta lista como favoritos?" — só faz sentido na origem
   * upload de planilha (tem `prompt_id`); nunca no chat simples nem quando a
   * negociação já veio de uma lista de favoritos existente. */
  public mostrarSalvarFavoritos = false;
  public nomeFavoritos = '';
  public salvandoFavoritos = false;

  /** "Quer transformar isso em uma compra de rotina?" — mesmo gatilho de
   * `mostrarSalvarFavoritos` (só existe `prompt_id` no fluxo de upload de
   * planilha; nunca no chat simples nem quando a negociação já veio de uma
   * lista de favoritos existente, que tem sua própria entrada via
   * `tornarListaRecorrente()` nas telas de favoritos — ver T-261). */
  public mostrarTornarRecorrente = false;
  /** `prompt_id` da lista de compras que originou este lote — vira
   * `origem.prompt_id` do modal em modo parametrizado. */
  private promptIdOrigem: string | number | null = null;
  /** Itens/origem preparados por `abrirTornarRecorrente()` para o 4º
   * `mat-step` (`app-tornar-recorrente-form`, T-271) — substitui o antigo
   * `MatDialog` aninhado (T-270/T-271). */
  public itensParaRecorrencia: ItemCompraRecorrente[] = [];
  public origemRecorrente: OrigemCompraRecorrente | null = null;

  public condicoesProdutos = [
    { label: 'Novo', codigo: 'novo' },
    { label: 'Usado', codigo: 'usado' }
  ];

  public duracoes = [
    { label: '1 Dia', codigo: '1' },
    { label: '3 Dias', codigo: '3' },
    { label: '5 Dias', codigo: '5' },
    { label: '7 Dias', codigo: '7' }
  ];

  constructor(
    public dialogRef: MatDialogRef<FullscreenModalContextualSearchComponent>,
    private router: Router,
    public firebase: FirebaseService,
    private formBuilder: FormBuilder,
    private functions: FunctionsService,
    private messageService: MessageService,
    @Inject(MAT_DIALOG_DATA) public data: { initialProducts: ProdutoCompostoInicial[], isBulkImport?: boolean, pending?: boolean }
  ) {
    this.initialProducts = data?.initialProducts || [];
    this.isBulkImport = data?.isBulkImport || false;
    this.uploading = !!data?.pending;
  }

  ngOnInit(): void {
    this.usuarioJaTemUmaConta = false;
    this.usuarioEstaLogado = !!this.firebase.user;

    this.negociacaoFormGroup = this.formBuilder.group({
      products: this.formBuilder.array([
        {
          duracao: [{ value: { label: '3 Dias', codigo: '3' }, disabled: false }, [Validators.required,]],
        }
      ])
    });
    this.initializeProducts();
  }

  ngAfterViewChecked(): void {
    // No message history to scroll here, but kept for consistency
  }

  /** Fluxo de importação em lote (upload de arquivo): mais itens do que o
   * chat de busca contextual jamais produz (máx. 10), ou sinalizado
   * explicitamente por quem abriu o componente. */
  get isBulkFlow(): boolean {
    return this.isBulkImport || this.initialProducts.length > LIMITE_ITENS_CHAT;
  }

  get productFormArray(): FormArray {
    return this.negociacaoFormGroup.get('products') as FormArray;
  }

  getProductFormGroup(index: number): UntypedFormGroup {
    return this.productFormArray?.controls?.[index] as UntypedFormGroup;
  }

  initializeProducts(): void {
    this.productFormArray.clear();
    this.productCandidates = [];
    if (this.initialProducts?.length > 0) {
      this.initialProducts?.forEach(product => {
        // Quando a importação em lote encontra exatamente 1 candidato no
        // catálogo, aplica ele automaticamente (mesmo dado que
        // `selecionarCandidato()` usaria se houvesse 2+ para escolher) — sem
        // isso, `ean` (campo obrigatório) ficava vazio pra qualquer item sem
        // EAN explícito na fonte, mesmo tendo dado match único no catálogo.
        const candidatoUnico = product?.candidatos?.length === 1 ? product.candidatos[0] : null;
        this.addProduct(
          candidatoUnico?.ean || product?.ean,
          candidatoUnico?.nome || product?.nome,
          candidatoUnico?.imagem?.url || product?.imagemUrl,
          product?.prompt_id,
          product?.quantidade,
          product?.valor_pretendido
        );
        this.productCandidates.push(product?.candidatos || []);
      });
    } else if (!this.uploading) {
      this.addProduct();
      this.productCandidates.push([]);
    }
  }

  addProduct(ean?: string, nome?: string, imagemUrl?: string, prompt_id?: number | string, quantidade?: number, valor_pretendido?: number): void {
    const productGroup = this.formBuilder.group({
      ean: [ean || '', Validators.required],
      nome: [nome || ''],
      condicao: ['novo', Validators.required],
      duracao: ['3', Validators.required],
      quantidade: [quantidade || 1, [Validators.required, Validators.min(1), Validators.max(100)]],
      valor_pretendido: [valor_pretendido || '', [Validators.min(1), Validators.max(999999.99)]],
      encerrar_automaticamente: [false, Validators.required],
      imagemUrl: [imagemUrl || ''],
      prompt_id: [prompt_id || undefined]
    });
    this.productFormArray.push(productGroup);
  }

  removeProduct(index: number): void {
    this.productFormArray.removeAt(index);
    this.productCandidates.splice(index, 1);
    if (this.productFormArray.length === 0) {
      this.addProduct();
      this.productCandidates.push([]);
    }
  }

  /** Ao escolher um candidato no seletor (`ProdutoCandidatoSelectorComponent`),
   * popula `ean`/`nome`/`imagemUrl` da linha — mesmo padrão de dado que
   * `addProduct()` já usa. */
  selecionarCandidato(index: number, candidato: { ean: string; nome: string; imagem?: { url: string } }): void {
    const productGroup = this.getProductFormGroup(index);
    if (!productGroup || !candidato) {
      return;
    }
    productGroup.patchValue({
      ean: candidato.ean,
      nome: candidato.nome,
      imagemUrl: candidato.imagem?.url || '',
    });
  }

  /** Chamado por `UploadListaComprasComponent` assim que a extração/cruzamento
   * com o catálogo terminar com sucesso — encerra o estado de processamento
   * e popula a revisão. */
  receiveBulkImportResult(itens: ProdutoCompostoInicial[]): void {
    this.initialProducts = itens || [];
    this.isBulkImport = true;
    this.uploading = false;
    this.uploadError = null;
    this.initializeProducts();
  }

  /** Chamado por `UploadListaComprasComponent` se o upload/extração falhar —
   * mantém o modal aberto (em vez de fechar silenciosamente) mostrando o
   * motivo, já que o comprador não tem mais o modal de upload para ver o erro. */
  receiveBulkImportError(message: string): void {
    this.uploading = false;
    this.uploadError = message;
  }

  registroFinalizado(resultado: string): void {
    if (resultado === 'login') {
      this.usuarioJaTemUmaConta = true;
      this.usuarioEstaLogado = false;
    } else if (resultado === 'finalizado') {
      this.negociacaoStepper.next();
    }
  }

  acessoFinalizado(resultado: string): void {
    if (resultado === 'registrar') {
      this.usuarioJaTemUmaConta = false;
      this.usuarioEstaLogado = false;
    } else if (resultado === 'finalizado') {
      this.negociacaoStepper.next();
    }
  }

  irParaNegociacao(): void {
    this.router.navigateByUrl(`/minha-conta`).finally(() => {
      this.dialogRef.close();
      this.closeModal();
    });
  }

  private buildNegociacaoPayload(productGroup: any): any {
    const payload: any = {
      novo: false,
      usado: false
    };

    Object.keys(productGroup.controls).forEach(key => {
      payload[key] = productGroup.controls[key].value;
    });

    payload[productGroup.controls['condicao'].value] = true;

    if (payload.valor_pretendido === '') {
      payload.valor_pretendido = 0;
    }

    return payload;
  }

  /** Cria uma negociação com retry — falhas por contenção de escrita no
   * Firestore (`Transaction lock timeout` e afins) são transientes: uma
   * segunda tentativa alguns instantes depois costuma passar sem que o
   * comprador precise refazer nada manualmente. */
  private async criarNegociacaoComRetry(payload: any, index: number): Promise<void> {
    for (let tentativa = 0; ; tentativa++) {
      try {
        return await this.criarNegociacao(payload, index);
      } catch (error) {
        if (tentativa >= TENTATIVAS_EXTRAS_POR_ITEM) {
          throw error;
        }
        await aguardar(ESPERA_ENTRE_TENTATIVAS_MS);
      }
    }
  }

  private criarNegociacao(payload: any, index: number): Promise<void> {
    if (!payload?.prompt_id) {
      delete payload.prompt_id;
      return this.functions.addNegociacao(payload).toPromise()
        .then(res => {
          if (index === 0) this.negociacao = res.body.data;
        });
    }

    return this.functions.addNegociacaoComposta(payload).toPromise()
      .then(res => {
        if (index === 0) this.negociacao = res.body.data;

        // Best-effort (E-42): grava na lista de compras persistida qual
        // candidato foi escolhido e a negociação criada para este item —
        // telemetria complementar. A negociação em si já foi criada com
        // sucesso acima; uma falha aqui nunca a reverte nem conta como
        // falha de criação no `criacaoResumo` — por isso não é `await`ada
        // (roda em segundo plano) e resolve o próprio erro.
        //
        // `adicionaNegociacao` (inversu-api) responde
        // `new Response(CREATED, null, 'Sucesso', negociacoesRef.id)` — o
        // campo `data` é a própria STRING do uid, não `{uid}` (bug real
        // encontrado em QA: `res.body.data.uid` é sempre `undefined`, então
        // esta chamada nunca disparava).
        const negociacaoUid = res?.body?.data;
        if (negociacaoUid) {
          this.functions.atualizarItemListaCompras(payload.prompt_id, index, {
            ean_selecionado: payload.ean,
            negociacao_uid: negociacaoUid,
          }).toPromise().catch(err => console.warn(`Não foi possível registrar o item ${index} na lista de compras ${payload.prompt_id}:`, err));
        }
      });
  }

  /** Nomes das linhas com campo obrigatório vazio (hoje, na prática, sempre
   * `ean` — não há input pra editá-lo manualmente na tela, então a única
   * saída para uma linha "sem correspondência" é removê-la). */
  private nomesItensInvalidos(): string[] {
    return this.productFormArray.controls
      .filter((productGroup: any) => productGroup.invalid)
      .map((productGroup: any, index: number) => productGroup.get('nome')?.value || `Item ${index + 1}`);
  }

  iniciaNegociacao(): void {
    if (this.negociacaoFormGroup.invalid) {
      this.negociacaoFormGroup.updateValueAndValidity();
      this.negociacaoFormGroup.markAllAsTouched();
      const nomesInvalidos = this.nomesItensInvalidos();
      this.messageService.add({
        severity: 'warn',
        summary: 'Revise os itens da lista',
        detail: nomesInvalidos.length
          ? `Sem correspondência no catálogo ainda: ${nomesInvalidos.join(', ')}. Remova esses itens (botão Excluir) para continuar.`
          : 'Por favor, preencha todos os campos obrigatórios.',
        life: 8000,
      });
      return;
    }

    this.loading = true;
    this.criacaoResumo = null;
    this.negociacaoFormGroup.disable();

    if (!this.isBulkFlow) {
      // Comportamento original, preservado tal qual: o chat de busca
      // contextual nunca passa de 10 itens, então um único `Promise.all`
      // continua adequado — não precisa dos lotes/resumo abaixo.
      const promises = this.productFormArray.controls.map((productGroup: any, index: number) =>
        this.criarNegociacao(this.buildNegociacaoPayload(productGroup), index)
          .catch(error => {
            console.error(`Error adding negotiation for product ${index + 1}:`, error);
            throw error;
          })
      );

      Promise.all(promises)
        .then(() => {
          this.negociacaoStepper.next();
        })
        .catch(() => {
          this.messageService.add({
            severity: 'error',
            summary: 'Não foi possível concluir',
            detail: 'Não conseguimos finalizar as negociações. Por favor, tente novamente mais tarde.',
            life: 8000,
          });
        })
        .finally(() => {
          this.loading = false;
          this.negociacaoFormGroup.enable();
        });
      return;
    }

    // Fluxo de importação em lote: dezenas/centenas de itens contra o máximo
    // de 10 do chat. Em vez de um único `Promise.all` (all-or-nothing e sem
    // rastreio, escondendo sucessos parciais reais já gravados no Firestore
    // em caso de falha — ver plano da feature), dispara lotes concorrentes
    // menores, sequenciados, rastreando sucesso/falha item a item.
    const controls = this.productFormArray.controls as any[];
    this.criacaoProgresso = { concluido: 0, total: controls.length };
    const falhas: { index: number; nome: string; error: any }[] = [];
    let sucessos = 0;

    const processarLotes = async (): Promise<void> => {
      for (let inicio = 0; inicio < controls.length; inicio += TAMANHO_LOTE_CRIACAO) {
        const lote = controls.slice(inicio, inicio + TAMANHO_LOTE_CRIACAO);
        const resultados = await Promise.allSettled(
          lote.map((productGroup, offset) => this.criarNegociacaoComRetry(this.buildNegociacaoPayload(productGroup), inicio + offset))
        );

        resultados.forEach((resultado, offset) => {
          const index = inicio + offset;
          this.criacaoProgresso = { concluido: this.criacaoProgresso.concluido + 1, total: controls.length };
          if (resultado.status === 'fulfilled') {
            sucessos++;
          } else {
            const nome = controls[index]?.get?.('nome')?.value || `#${index + 1}`;
            falhas.push({ index, nome, error: resultado.reason });
            console.error(`Error adding negotiation for product ${index + 1}:`, resultado.reason);
          }
        });
      }
    };

    processarLotes()
      .then(() => {
        this.criacaoResumo = {
          total: controls.length,
          sucesso: sucessos,
          falhas: falhas.length,
          itensComFalha: falhas,
        };

        // Best-effort (E-42): fecha a lista de compras persistida com o
        // resumo final do lote. Só existe lista a concluir no fluxo bulk
        // (o `prompt_id` do primeiro item é o id da lista); nunca deve
        // travar a navegação pro passo de resumo, por isso não é `await`ada.
        const promptId = controls[0]?.get('prompt_id')?.value;
        if (promptId) {
          this.functions.concluirListaCompras(promptId, {
            total: controls.length,
            sucesso: sucessos,
            falhas: falhas.length,
            itens_com_falha: falhas.map(falha => ({ index: falha.index, nome: falha.nome })),
          }).toPromise().catch(err => console.warn(`Não foi possível concluir a lista de compras ${promptId}:`, err));

          // Pedido do time de negócio (E-42, revisado): não uma tela de
          // acompanhamento — só oferecer salvar a lista na feature de
          // favoritos já existente. Só aparece aqui (origem = upload de
          // planilha, `prompt_id` existe); nunca no chat simples nem quando a
          // negociação já veio de uma lista de favoritos.
          this.mostrarSalvarFavoritos = true;

          // E-44: mesmo gatilho — oferecer transformar esse lote numa
          // compra de rotina. As duas flags nascem `true` juntas, mas o
          // template (T-279/UX-3) sequencia a exibição: o card de
          // recorrência só aparece depois que o de favoritos for
          // respondido (`!mostrarSalvarFavoritos` no *ngIf) — nunca os
          // dois empilhados ao mesmo tempo.
          this.mostrarTornarRecorrente = true;
          this.promptIdOrigem = promptId;
        }

        this.negociacaoStepper.next();
      })
      .finally(() => {
        this.loading = false;
        this.criacaoProgresso = null;
        this.negociacaoFormGroup.enable();
      });
  }

  closeModal(): void {
    this.isOpen = false;
    this.closed.emit();
    this.dialogRef.close();
  }

  /**
   * Cria a lista de favoritos com o nome informado e adiciona todos os
   * produtos do lote (independente de a negociação de cada um ter sido
   * criada com sucesso — é a "lista de compras" inteira que o comprador
   * montou, não só o que virou negociação de fato).
   *
   * Chamadas SEQUENCIAIS de propósito: `clientAddFavoriteToList`
   * (`inversu-api`) faz leitura+escrita do documento `clientes/{uid}` sem
   * transação — disparar em paralelo arriscaria uma escrita concorrente
   * sobrescrever a outra e perder produtos.
   */
  private async salvarProdutosComoFavoritos(nome: string, eans: string[]): Promise<void> {
    const [primeiroEan, ...demaisEans] = eans;
    await this.functions.clientAddFavoritesList({ name: nome, ean: primeiroEan }).toPromise();
    for (const ean of demaisEans) {
      await this.functions.clientAddFavoriteToList({ ean, name: nome }).toPromise();
    }
  }

  salvarComoFavoritos(): void {
    const nome = this.nomeFavoritos?.trim();
    if (!nome || this.salvandoFavoritos) {
      return;
    }

    const eans = Array.from(new Set(
      (this.productFormArray.controls as any[])
        .map(control => control.get('ean')?.value)
        .filter(Boolean)
    )) as string[];

    if (!eans.length) {
      this.mostrarSalvarFavoritos = false;
      return;
    }

    this.salvandoFavoritos = true;
    this.salvarProdutosComoFavoritos(nome, eans)
      .then(() => {
        this.messageService.add({
          severity: 'success',
          summary: 'Lista salva nos favoritos',
          detail: `Seus produtos foram salvos em "${nome}".`,
          life: 6000,
        });
      })
      .catch(error => {
        console.warn('Não foi possível salvar a lista como favoritos:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Não foi possível salvar',
          detail: 'Você pode tentar novamente pela tela de Favoritos.',
          life: 8000,
        });
      })
      .finally(() => {
        this.salvandoFavoritos = false;
        this.mostrarSalvarFavoritos = false;
      });
  }

  dispensarSalvarFavoritos(): void {
    this.mostrarSalvarFavoritos = false;
  }

  /**
   * Prepara o modo **parametrizado** do 4º `mat-step` (`app-tornar-recorrente-form`,
   * T-271) e avança o stepper até ele — os itens já têm quantidade/condição/
   * duração/valor definidos pelo comprador no passo 2, então reaproveita
   * `buildNegociacaoPayload` — o mesmo payload congelado que `criarNegociacao`
   * já usa — em vez de remontar o formato à mão (ver T-260, `ItemCompraRecorrente`).
   *
   * Antes da T-271 isto abria `TornarRecorrenteModalComponent` como um
   * `MatDialog` aninhado dentro deste próprio `MatDialog` — diálogo empilhado,
   * frágil para acessibilidade (dois focus-trap encadeados). Agora é só mais
   * um passo do mesmo stepper.
   */
  abrirTornarRecorrente(): void {
    this.itensParaRecorrencia = (this.productFormArray.controls as any[]).map(productGroup => this.buildNegociacaoPayload(productGroup));
    this.origemRecorrente = { tipo: 'bulk-list-import', prompt_id: this.promptIdOrigem };
    this.negociacaoStepper.next();
  }

  dispensarTornarRecorrente(): void {
    this.mostrarTornarRecorrente = false;
  }
}
